import { Decimal, formatMinutes, formatPercentage, formatQuantity } from "@normax-performance/calculation-engine";
import { dotToCommaDecimal, formatPerformanceRatioLabel, formatQuantityLabel } from "@normax-performance/shared-format";

/**
 * Audit History "honest diff" values arrive as RAW decimal strings — the
 * backend DTO deliberately does not pre-format them (server sends stable
 * data, frontend presents it — see
 * docs/NORMAX_PERFORMANCE_DAILY_ENTRY_AUDIT_HISTORY_IMPLEMENTATION_REPORT.md
 * §3). For `plannedQuantityRaw`/`performanceRatioRaw` specifically, "raw"
 * means genuinely unrounded: these are `Decimal.toString()` snapshots taken
 * BEFORE the value is ever written to a fixed-scale DB column, so they can
 * carry decimal.js's full internal precision (e.g. "33.86666666666667",
 * "1.1811023622047244") — the exact production complaint this module fixes
 * (docs/NORMAX_PERFORMANCE_DAILY_ENTRY_AUDIT_HISTORY_UI_POLISH_REPORT.md).
 *
 * Every rounding call below REUSES the app's existing, LOCKED rounding
 * contract (`@normax-performance/calculation-engine`'s rounding.ts — 2
 * decimals, HALF_AWAY_FROM_ZERO) — the SAME functions that already produce
 * `WorkLineDto.plannedQuantityLabel`/`.performanceRatioLabel` elsewhere in
 * this app (see `daily-entry.mapper.ts` server-side, `useLinePreview.ts`
 * client-side). No new rounding algorithm is introduced here.
 */

/** `durationMinutes` — always whole minutes in this app; rounds defensively rather than assuming, then appends " min" at the call site (not here, so the raw value stays testable on its own). */
export function formatDurationMinutesValue(raw: string): string {
  return dotToCommaDecimal(formatMinutes(new Decimal(raw)));
}

/** `plannedQuantityRaw` — ALWAYS exactly 2 decimals (never trimmed), matching how this same underlying value is displayed everywhere else in the app (e.g. "39,20", never "39,2"). */
export function formatPlannedQuantityValue(raw: string): string {
  const rounded = formatQuantity(new Decimal(raw));
  return formatQuantityLabel(rounded) ?? "—";
}

/**
 * `reportedQuantity` — a user-entered value, not a server-computed one.
 * "Maximum 2 decimal places, avoid unnecessary long precision" (approved UI
 * polish spec §7) — capped at 2 decimals but NOT padded, so a clean whole
 * number stays "50", not "50,00" (`Decimal#toDecimalPlaces().toString()`
 * trims trailing zeros; `toFixed()` would pad them — deliberately using the
 * former here, unlike `formatPlannedQuantityValue` above).
 */
export function formatReportedQuantityValue(raw: string): string {
  const rounded = new Decimal(raw).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  return dotToCommaDecimal(rounded.toString());
}

/** `performanceRatioRaw` — a RATIO (e.g. 1.02), never a percentage-scale value. Converts ×100 and formats as "102,04%", reusing the exact same `formatPercentage`/`formatPerformanceRatioLabel` pair used for every other performance-ratio display in this app. */
export function formatPerformanceRatioValue(raw: string): string {
  const percentRounded = formatPercentage(new Decimal(raw));
  return formatPerformanceRatioLabel(percentRounded) ?? "—";
}
